import { forwardRef, PropsWithoutRef, useEffect, useImperativeHandle, useState } from 'react';
import { createZodFormResolver, lt, showSuccessAlert, useFeatureFlag, useFormControls } from '@mews/framework';
import { Icon, IconName, SelectionCardGroup, SelectionCardGroupProps, Stack } from '@optimus-web/core';
import { localizeCurrencyValue } from '@mews-commander/utils';
import { DataTestId, FeatureFlag } from '@mews-commander/enums';
import { SuspenseBoundary } from '@mews-commander/ui';
import { PaymentFormProps } from '../types';
import { useDefaultFormContext } from '../useDefaultFormValues';
import { useAddPaymentRequest, useAddPreauthorizationRequest } from '../queries';
import { useInvalidateGetAllPaymentRequests } from '../../../tables/PaymentRequest/hooks';
import {
    PaymentRequestType,
    PaymentRequestFormField,
    createPaymentRequestFormSchema,
    PaymentRequestFormValues,
} from './schema';
import { PaymentRequestTypeSelection } from './PaymentRequestTypeSelection';
import { CollectPaymentFormFields } from './fields/CollectPaymentFormFields';

const selectionCardGroupOptionsMap: Record<PaymentRequestType, SelectionCardGroupProps['options'][number]> = {
    [PaymentRequestType.PaymentMethod]: {
        label: lt('ProcessPaymentRequestCollectPaymentMethodOptionLabel'),
        description: lt('ProcessPaymentRequestCollectPaymentMethodOptionDescription'),
        value: PaymentRequestType.PaymentMethod,
        name: PaymentRequestType.PaymentMethod,
        media: <Icon value={IconName.PaymentCard} />,
        dataTestId: DataTestId.PaymentRequestCollectPaymentMethodOption,
    },
    [PaymentRequestType.Payment]: {
        label: lt('ProcessPaymentRequestCollectPaymentOptionLabel'),
        description: lt('ProcessPaymentRequestCollectPaymentOptionDescription'),
        value: PaymentRequestType.Payment,
        name: PaymentRequestType.Payment,
        media: <Icon value={IconName.PaymentRequest} />,
        dataTestId: DataTestId.PaymentRequestCollectPaymentOption,
    },
    [PaymentRequestType.Preauthorization]: {
        label: lt('ProcessPaymentRequestPreauthorizePaymentOptionLabel'),
        description: lt('ProcessPaymentRequestPreauthorizePaymentOptionDescription'),
        value: PaymentRequestType.Preauthorization,
        name: PaymentRequestType.Preauthorization,
        media: <Icon value={IconName.Preauthorization} />,
        dataTestId: DataTestId.PaymentRequestPreauthorizePaymentOption,
    },
};

export const PaymentRequestForm = forwardRef(
    ({ accountId, onSuccess, onSubmitting, onSubmitted }: PropsWithoutRef<PaymentFormProps>, ref) => {
        const isPaymentRequestCollectTypeEnabled = useFeatureFlag(FeatureFlag.paymentRequestCollectTypeEnabled);
        const invalidateGetAllPaymentRequests = useInvalidateGetAllPaymentRequests();

        const { defaultFormValues: defaultValues, allowedPaymentRequestTypeOptions } = useDefaultFormContext();
        const resolver = createZodFormResolver(createPaymentRequestFormSchema(isPaymentRequestCollectTypeEnabled));

        const addPaymentRequest = useAddPaymentRequest({ accountId });
        const addPreauthorizationRequest = useAddPreauthorizationRequest({ accountId });

        const [paymentRequestType, setPaymentRequestType] = useState(
            allowedPaymentRequestTypeOptions.length === 1
                ? allowedPaymentRequestTypeOptions[0]
                : PaymentRequestType.Payment
        );

        const { renderFormContent, submit, hasDirtyFields, reset, setValue } =
            useFormControls<PaymentRequestFormValues>({
                onSubmit: async (formValues) => {
                    try {
                        onSubmitting?.();

                        switch (formValues.type) {
                            case PaymentRequestType.Payment:
                                await addPaymentRequest(formValues);
                                showSuccessAlert({
                                    description: lt('PaymentRequestCreatedSuccessMessage', {
                                        amount: localizeCurrencyValue({
                                            value: formValues.amount,
                                            currency: formValues.currencyCode,
                                        }),
                                    }),
                                });

                                break;
                            case PaymentRequestType.Preauthorization:
                                await addPreauthorizationRequest(formValues);
                                break;
                            case PaymentRequestType.PaymentMethod:
                                // TODO: FINTECH-7196
                                break;
                            default:
                                break;
                        }
                        invalidateGetAllPaymentRequests({ accountId });

                        onSuccess?.();
                    } finally {
                        onSubmitted?.();
                    }
                },
                defaultValues,
                resolver,
            });

        useImperativeHandle(
            ref,
            () => ({
                submit,
                hasDirtyFields,
            }),
            [submit, hasDirtyFields]
        );

        useEffect(() => {
            setValue(PaymentRequestFormField.Type, paymentRequestType);
        }, [setValue, paymentRequestType]);

        return (
            <>
                {isPaymentRequestCollectTypeEnabled && allowedPaymentRequestTypeOptions.length > 1 && (
                    <SelectionCardGroup
                        label={lt('ProcessPaymentRequestCollectionTypeLabel')}
                        type="radio"
                        inputValue={paymentRequestType}
                        cardVariant="horizontal"
                        options={allowedPaymentRequestTypeOptions.map((type) => selectionCardGroupOptionsMap[type])}
                        onChange={(_e, value) => {
                            setPaymentRequestType(value as PaymentRequestType);
                            reset();
                        }}
                    />
                )}
                {renderFormContent(
                    <Stack spacing="300" vertical>
                        {isPaymentRequestCollectTypeEnabled && (
                            <SuspenseBoundary loader="inline">
                                <PaymentRequestTypeSelection accountId={accountId} />
                            </SuspenseBoundary>
                        )}
                        {!isPaymentRequestCollectTypeEnabled && <CollectPaymentFormFields accountId={accountId} />}
                    </Stack>
                )}
            </>
        );
    }
);
